import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { SettingsProvider } from "./lib/SettingsContext";

import PublicLayout from "./layout/PublicLayout";
import Home from "./pages/Home";
import About from "./pages/About";
import Academic from "./pages/Academic";
import SchoolLife from "./pages/SchoolLife";
import Achievements from "./pages/Achievements";
import { NewsList, NewsDetail } from "./pages/News";
import PPDB from "./pages/PPDB";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import Teachers from "./pages/Teachers";
import Facilities from "./pages/Facilities";
import { Announcements, CalendarPage, Downloads, PortalPlaceholder } from "./pages/MiscPages";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import { NewsManage, AnnouncementsManage, TeachersManage, AchievementsManage, ExtracurricularsManage, GalleryManage, FacilitiesManage, EventsManage, DocumentsManage } from "./pages/admin/CRUDConfigs";
import { PPDBManage, MessagesManage, SettingsManage } from "./pages/admin/SpecialPages";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Memuat...</div>;
  if (!user) return <Navigate to="/admin" replace />;
  return children;
}

function PublicRoute({ Component }) {
  return <PublicLayout><Component /></PublicLayout>;
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <ScrollTop />
          <Toaster position="top-center" richColors />
          <Routes>
            {/* Public */}
            <Route path="/" element={<PublicRoute Component={Home} />} />
            <Route path="/tentang-kami" element={<PublicRoute Component={About} />} />
            <Route path="/akademik" element={<PublicRoute Component={Academic} />} />
            <Route path="/kehidupan-sekolah" element={<PublicRoute Component={SchoolLife} />} />
            <Route path="/prestasi" element={<PublicRoute Component={Achievements} />} />
            <Route path="/berita" element={<PublicRoute Component={NewsList} />} />
            <Route path="/berita/:id" element={<PublicRoute Component={NewsDetail} />} />
            <Route path="/ppdb" element={<PublicRoute Component={PPDB} />} />
            <Route path="/galeri" element={<PublicRoute Component={Gallery} />} />
            <Route path="/kontak" element={<PublicRoute Component={Contact} />} />
            <Route path="/guru" element={<PublicRoute Component={Teachers} />} />
            <Route path="/fasilitas" element={<PublicRoute Component={Facilities} />} />
            <Route path="/pengumuman" element={<PublicRoute Component={Announcements} />} />
            <Route path="/kalender" element={<PublicRoute Component={CalendarPage} />} />
            <Route path="/download-center" element={<PublicRoute Component={Downloads} />} />
            <Route path="/portal/murid" element={<PublicLayout><PortalPlaceholder title="Portal Murid" /></PublicLayout>} />
            <Route path="/portal/guru" element={<PublicLayout><PortalPlaceholder title="Portal Guru" /></PublicLayout>} />

            {/* Admin */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<RequireAdmin><AdminLayout><Dashboard /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/news" element={<RequireAdmin><AdminLayout><NewsManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/announcements" element={<RequireAdmin><AdminLayout><AnnouncementsManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/ppdb" element={<RequireAdmin><AdminLayout><PPDBManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/teachers" element={<RequireAdmin><AdminLayout><TeachersManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/achievements" element={<RequireAdmin><AdminLayout><AchievementsManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/extracurriculars" element={<RequireAdmin><AdminLayout><ExtracurricularsManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/gallery" element={<RequireAdmin><AdminLayout><GalleryManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/facilities" element={<RequireAdmin><AdminLayout><FacilitiesManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/events" element={<RequireAdmin><AdminLayout><EventsManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/documents" element={<RequireAdmin><AdminLayout><DocumentsManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/messages" element={<RequireAdmin><AdminLayout><MessagesManage /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/settings" element={<RequireAdmin><AdminLayout><SettingsManage /></AdminLayout></RequireAdmin>} />

            <Route path="*" element={<PublicLayout><div className="pt-32 pb-20 text-center"><h1 className="font-serif text-6xl font-bold text-[#0B1D3A]">404</h1><p className="mt-4 text-slate-500">Halaman tidak ditemukan</p></div></PublicLayout>} />
          </Routes>
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
